import { ICorporationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useQuery } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounce } from 'custom_hooks/useDebounce';
import { DashboardHeader } from 'pages/Dashboard/components/DashboardHeader';
import { LocationFilterButton } from 'pages/Dashboard/components/filter_button/LocationFilterButton';
import {
    DashboardTimeRangePreset,
    presetToDateRange,
    TimeRangeFilterButton,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/TimeRangeFilterButton';
import { memo, useCallback, useMemo } from 'react';

import { ValueByLocationDashboardLayout } from './components/styled';
import { ValueByLocationCard } from './components/ValueByLocationCard';
import { useValueByLocationFiltersStore } from './store/useValueByLocationFiltersStore';

export type ValueByLocationDashboardProps = Readonly<{
    corporation: ICorporationResponse;
}>;

export const ValueByLocationDashboard = memo(function ValueByLocationDashboard({
    corporation,
}: ValueByLocationDashboardProps) {
    const {
        startDateTime,
        endDateTime,
        locationIds,
        timeRange,
        setLocationIds,
        setTimeRange,
        setStartDateTime,
        setEndDateTime,
    } = useValueByLocationFiltersStore();

    // debounce to avoid pummeling the API during time-range drag
    const debouncedStartDateTime = useDebounce(startDateTime, 300);

    const handleTimeRangeChange = useCallback(
        (preset: DashboardTimeRangePreset) => {
            setTimeRange(preset);
            const { startDateTime, endDateTime } = presetToDateRange(preset);
            setStartDateTime(startDateTime);
            if (endDateTime) {
                setEndDateTime(endDateTime);
            }
        },
        [setTimeRange, setStartDateTime, setEndDateTime]
    );

    const queryKey = useMemo(
        () => [
            'valueByLocation',
            corporation.Id,
            debouncedStartDateTime.toISOString(),
            endDateTime.toISOString(),
            locationIds.join(','),
        ],
        [corporation.Id, debouncedStartDateTime, endDateTime, locationIds]
    );

    const { data, isLoading, error } = useQuery({
        queryKey,
        queryFn: () =>
            sdClient.getValueByLocationForCorporation({
                corporationId: corporation.Id,
                startDateTime: debouncedStartDateTime,
                endDateTime: endDateTime,
                locationIds: locationIds.length > 0 ? locationIds : undefined,
            }),
        placeholderData: (prev) => prev,
    });

    return (
        <ValueByLocationDashboardLayout>
            <DashboardHeader
                corporation={corporation}
                currentDashboard="value-by-location"
            >
                <LocationFilterButton
                    corporationId={corporation.Id}
                    locationIds={locationIds}
                    onChangeLocationIds={setLocationIds}
                />
                <TimeRangeFilterButton
                    selectedPreset={timeRange}
                    onChangePreset={handleTimeRangeChange}
                />
            </DashboardHeader>
            {error && (
                <div
                    style={{
                        gridArea: 'value-by-location',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <p>Error loading dashboard data: {error.message}</p>
                </div>
            )}
            {(isLoading || !data) && (
                <div style={{ gridArea: 'value-by-location' }}>
                    <SpinnerArea />
                </div>
            )}
            {data && <ValueByLocationCard data={data} />}
        </ValueByLocationDashboardLayout>
    );
});
